// components/masterdata/EigentuemerListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const EigentuemerListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [eigentuemer, setEigentuemer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newEigentuemer, setNewEigentuemer] = useState({
    name: "",
    vorname: "",
    email: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchEigentuemer = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/eigentuemer`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Eigentuemer');
      }

      const data = await response.json();
      setEigentuemer(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/eigentuemer/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchEigentuemer();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diesen Eigentuemer wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/eigentuemer/${id}/`, {
        method: "DELETE",
      });
      fetchEigentuemer(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddEigentuemer = async () => {
    if (!newEigentuemer.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/eigentuemer/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newEigentuemer)
      });

      if (response.ok) {
        setNewEigentuemer({ name: "", vorname: "", email: "" });
        fetchEigentuemer();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchEigentuemer();
  }, [accessToken, fetchEigentuemer]);

  if (loading) return <div>Lade Eigentuemer...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Eigentümer verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neuer Eigentümer..."
            value={newEigentuemer.name}
            onChange={(e) => setNewEigentuemer({ ...newEigentuemer, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Vorname"
            value={newEigentuemer.vorname}
            onChange={(e) => setNewEigentuemer({ ...newEigentuemer, vorname: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="E-Mail"
            value={newEigentuemer.email}
            onChange={(e) => setNewEigentuemer({ ...newEigentuemer, email: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddEigentuemer} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Vorname</th>
              <th>E-Mail</th>
            </tr>
          </thead>
          <tbody>
            {eigentuemer.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Vorname */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.vorname || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], vorname: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.vorname
                  )}
                </td>

                {/* E-Mail */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.email || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], email: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.email
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              eigentuemer.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  vorname: k.vorname,
                  email: k.email,
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default EigentuemerListe;
